import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Typography,
  IconButton,
  ListSubheader,
  Snackbar,
  Alert,
} from '@mui/material';
import { X } from 'lucide-react';
import { MessageAttachment } from '../../types';
import { cases } from '../../data/sampleData';
import { caseStages } from '../../data/caseStages';
import { useCaseStore } from '../../store/caseStore';

interface AttachmentDialogProps {
  open: boolean;
  onClose: () => void;
  onAttach?: (files: File[], caseId?: string, stageId?: string) => void;
  currentCaseId?: string;
  attachment?: MessageAttachment;
}

export const AttachmentDialog: React.FC<AttachmentDialogProps> = ({
  open,
  onClose,
  onAttach,
  currentCaseId,
  attachment,
}) => {
  const [selectedCase, setSelectedCase] = useState(currentCaseId || '');
  const [selectedStage, setSelectedStage] = useState('');
  const [showSuccess, setShowSuccess] = useState(false);
  const { updateCase } = useCaseStore();

  // Reset form when dialog opens
  useEffect(() => {
    if (open) {
      setSelectedCase(currentCaseId || '');
      setSelectedStage('');
      setShowSuccess(false);
    }
  }, [open, currentCaseId]);

  // Get the selected case's details
  const selectedCaseData = cases.find(c => c.id === selectedCase);

  // Get stages for the selected case
  const availableStages = selectedCase ? caseStages : [];

  // Group stages by category
  const stagesByCategory = availableStages.reduce((acc, stage) => {
    if (!acc[stage.category]) {
      acc[stage.category] = [];
    }
    acc[stage.category].push(stage);
    return acc;
  }, {} as Record<string, typeof caseStages>);

  const handleSubmit = () => {
    if (!selectedCase) return;

    if (attachment) {
      // Add the document to the case
      const newDocument = {
        id: `doc-${Date.now()}`,
        title: attachment.name,
        type: 'legal', // Default to legal document type
        url: attachment.url,
        uploadDate: new Date().toISOString(),
        uploadedBy: '1', // Current user ID
        size: attachment.size,
        stageId: selectedStage || undefined, // Link document to specific stage
      };

      // Add timeline event for document upload
      const timelineEvent = {
        id: `tl-${Date.now()}`,
        date: new Date().toISOString(),
        description: `Document uploaded: "${attachment.name}"${selectedStage ? ` to ${caseStages.find(s => s.id === selectedStage)?.name} stage` : ''}`,
        type: 'document-filed',
        addedBy: '1', // Current user ID
        documentId: newDocument.id,
        stageId: selectedStage || undefined,
      };

      // Update the case with new document and timeline event
      updateCase(selectedCase, {
        documents: [...(selectedCaseData?.documents || []), newDocument],
        timeline: [...(selectedCaseData?.timeline || []), timelineEvent],
      });

      // Show success message
      setShowSuccess(true);
      setTimeout(() => {
        handleClose();
      }, 1500);
    } else if (onAttach) {
      // Handle new file upload
      onAttach([], selectedCase, selectedStage || undefined);
      handleClose();
    }
  };

  const handleClose = () => {
    setSelectedCase(currentCaseId || '');
    setSelectedStage('');
    setShowSuccess(false);
    onClose();
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 2 }
        }}
      >
        <DialogTitle sx={{ m: 0, p: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="h6" component="div">
            Add Document to Case
          </Typography>
          <IconButton
            aria-label="close"
            onClick={handleClose}
            sx={{ color: 'text.secondary' }}
          >
            <X size={20} />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            {attachment && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Document Details
                </Typography>
                <Box sx={{ 
                  p: 2, 
                  bgcolor: 'background.default',
                  borderRadius: 1,
                  border: '1px solid',
                  borderColor: 'divider',
                }}>
                  <Typography variant="body1">
                    {attachment.name}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {(attachment.size / 1024 / 1024).toFixed(2)} MB
                  </Typography>
                </Box>
              </Box>
            )}

            <FormControl fullWidth>
              <InputLabel>Link to Case</InputLabel>
              <Select
                value={selectedCase}
                label="Link to Case"
                onChange={(e) => {
                  setSelectedCase(e.target.value);
                  setSelectedStage(''); // Reset stage when case changes
                }}
              >
                <MenuItem value="">
                  <em>None</em>
                </MenuItem>
                {cases.map((case_) => (
                  <MenuItem key={case_.id} value={case_.id}>
                    {case_.caseNumber} - {case_.title}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {selectedCase && (
              <Box>
                <FormControl fullWidth>
                  <InputLabel>Link to Stage</InputLabel>
                  <Select
                    value={selectedStage}
                    label="Link to Stage"
                    onChange={(e) => setSelectedStage(e.target.value)}
                    MenuProps={{
                      PaperProps: {
                        sx: { maxHeight: 400 },
                      },
                    }}
                  >
                    <MenuItem value="">
                      <em>None</em>
                    </MenuItem>
                    {Object.entries(stagesByCategory).map(([category, stages]) => [
                      <ListSubheader key={category}>{category}</ListSubheader>,
                      ...stages.map(stage => {
                        const isCurrentStage = stage.id === selectedCaseData?.currentStageId;
                        return (
                          <MenuItem 
                            key={stage.id} 
                            value={stage.id}
                            sx={{
                              pl: 4,
                              ...(isCurrentStage && {
                                fontWeight: 600,
                                bgcolor: 'action.selected',
                              }),
                            }}
                          >
                            {stage.name}
                            {isCurrentStage && (
                              <Typography
                                variant="caption"
                                component="span"
                                sx={{
                                  ml: 1,
                                  color: 'text.secondary',
                                  fontStyle: 'italic',
                                }}
                              >
                                (Current Stage)
                              </Typography>
                            )}
                          </MenuItem>
                        );
                      }),
                    ]).flat()}
                  </Select>
                </FormControl>
                {selectedStage && (
                  <Typography 
                    variant="caption" 
                    color="text.secondary"
                    sx={{ display: 'block', mt: 1 }}
                  >
                    {caseStages.find(s => s.id === selectedStage)?.description}
                  </Typography>
                )}
              </Box>
            )}
          </Box>
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={handleClose} color="inherit">
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={!selectedCase}
          >
            Add to Case
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={showSuccess}
        autoHideDuration={2000}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert severity="success" elevation={6} variant="filled">
          Document successfully added to case
        </Alert>
      </Snackbar>
    </>
  );
};